import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { timeout } from 'rxjs/operators';
import { StorageKey } from 'src/app/shared/constants/global-config.constant';
import { environment } from 'src/environments/environment';
import { VersionService } from '../version/version.service';

/** A report that has not answered by then is dropped; nobody is waiting on it. */
export const BUILD_REPORT_TIMEOUT_MS = 10000;

/** What a page reports when its main script carries no content hash (`ng serve`). */
export const UNHASHED_BUILD = 'dev';

/**
 * Tells the API which build of this app the photobooth is running, so one call
 * (`GET /application/branch/seen`) shows whether every booth has picked up a
 * deploy - without visiting a single booth.
 *
 * The version string is bumped by hand and two deploys can share it; the build
 * is the content hash in the main script's file name (`main.7debe88dfaf2a8c9.js`),
 * which changes with every build, so it proves which build actually loaded.
 *
 * Fire-and-forget like the banner events: no retry, no toast, no loading
 * overlay, and every failure is silent - a customer must never notice it.
 */
@Injectable({
  providedIn: 'root'
})
export class BuildReportService {

  constructor(
    private httpClient: HttpClient,
    private versionService: VersionService,
  ) { }

  /** `2.17.0` out of `2.17.0 PROD`. */
  appVersion(): string {
    return (this.versionService.version ?? '').split(' ')[0];
  }

  /** The content hash of the main script this page was loaded with. */
  build(doc: Document = document): string {
    const scripts = Array.from(doc.querySelectorAll('script[src]'));
    for (const script of scripts) {
      const match = /(?:^|\/)main\.([0-9a-f]{8,64})\.js(?:[?#].*)?$/.exec(script.getAttribute('src') ?? '');
      if (match) {
        return match[1];
      }
    }
    return UNHASHED_BUILD;
  }

  report(): void {
    const session = localStorage.getItem(StorageKey.SESSION_ID);
    if (!session) {
      return;
    }
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session}`,
    });
    this.httpClient.post(
      `${environment.apiEndpoint}/application/branch/seen`,
      { app_version: this.appVersion(), build: this.build() },
      { headers }
    ).pipe(
      timeout(BUILD_REPORT_TIMEOUT_MS)
    ).subscribe({
      error: () => { },
    });
  }
}
